import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Table, List } from "lucide-react";
import ServicePickerSelect from "./ServicePickerSelect";
import ServicePickerTable from "./ServicePickerTable";
import {
  ServiceEmailSolicitud,
  ServiceEmailSolicitudServiceLink,
  ServiceEmailSolicitudServicesPayload,
} from "../../../models/ServiceEmailSolicitudModel";
import { isEnaexCompany } from "../../../config/adicionalCompany";

type ServiceRow = {
  key: string;
  service: string;
  passenger_count: string;
};

type Props = {
  isOpen: boolean;
  solicitud?: ServiceEmailSolicitud | null;
  companyId?: number | null;
  companyName?: string | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: ServiceEmailSolicitudServicesPayload) => void | Promise<void>;
};

const createRow = (passengerCount = "1"): ServiceRow => ({
  key: `${Date.now()}-${Math.random()}`,
  service: "",
  passenger_count: passengerCount,
});

const ServiceEmailSolicitudServicesModal: React.FC<Props> = ({
  isOpen,
  solicitud,
  companyId,
  companyName,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [rows, setRows] = useState<ServiceRow[]>([createRow()]);
  const [submitted, setSubmitted] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "select">("table");
  const allowIdSearch = isEnaexCompany(companyName);

  const totalPax = solicitud?.passenger_count ?? 0;
  const alreadyAssigned = solicitud?.assigned_passenger_count ?? 0;
  const remainingBase =
    solicitud?.remaining_passenger_count ?? Math.max(0, totalPax - alreadyAssigned);

  useEffect(() => {
    if (!isOpen || !solicitud) return;
    setSubmitted(false);
    setRows([createRow(String(Math.max(1, remainingBase)))]);
    setViewMode("table");
  }, [isOpen, solicitud, remainingBase]);

  const newAssigned = useMemo(
    () => rows.reduce((sum, row) => sum + Math.max(0, Number(row.passenger_count) || 0), 0),
    [rows]
  );

  const assignedAfter = alreadyAssigned + newAssigned;
  const remainingAfter = Math.max(0, totalPax - assignedAfter);
  const countMatches = newAssigned === remainingBase;

  const selectedServiceIds = useMemo(
    () =>
      rows
        .map((row) => Number(row.service))
        .filter((id) => Number.isFinite(id) && id > 0),
    [rows]
  );

  const existingLinks: ServiceEmailSolicitudServiceLink[] = solicitud?.service_links ?? [];
  const alreadyLinkedIds = useMemo(
    () =>
      existingLinks
        .map((link) => Number(link.service_id ?? link.service))
        .filter((id) => Number.isFinite(id) && id > 0),
    [existingLinks]
  );

  if (!isOpen || !solicitud) return null;

  const updateRow = (key: string, patch: Partial<ServiceRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    setRows((prev) => {
      const used = prev.reduce(
        (sum, row) => sum + Math.max(0, Number(row.passenger_count) || 0),
        0
      );
      const left = Math.max(1, remainingBase - used);
      return [...prev, createRow(String(left))];
    });
  };

  const removeRow = (key: string) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.key !== key)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!countMatches) return;
    if (rows.some((row) => !row.service || Number(row.passenger_count) < 1)) return;

    const uniqueIds = new Set(selectedServiceIds);
    if (uniqueIds.size !== selectedServiceIds.length) return;

    await onSubmit({
      items: rows.map((row) => ({
        service: Number(row.service),
        passenger_count: Number(row.passenger_count),
        request_date: solicitud.request_date ?? null,
        request_time: solicitud.request_time ?? null,
        origin_text: solicitud.origin_text ?? "",
        destination_text: solicitud.destination_text ?? "",
      })),
    });
  };

  return (
    <div
      className="services-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-email-sol-services-title"
    >
      <form
        className="services-modal-inner services-modal-inner--wide service-email-modal"
        onSubmit={handleSubmit}
      >
        <header className="services-modal-header">
          <div className="services-modal-title-wrap">
            <h2 id="service-email-sol-services-title" className="services-modal-title">
              Asignar servicios — solicitud #{solicitud.id}
            </h2>
            <div className="service-picker-view-toggle" role="group" aria-label="Vista de servicios">
              <button
                type="button"
                className={viewMode === "table" ? "is-active" : ""}
                onClick={() => setViewMode("table")}
                title="Ver tabla con filtros"
              >
                <Table size={14} strokeWidth={2} />
                Tabla
              </button>
              <button
                type="button"
                className={viewMode === "select" ? "is-active" : ""}
                onClick={() => setViewMode("select")}
                title="Selector simple"
              >
                <List size={14} strokeWidth={2} />
                Selector
              </button>
            </div>
          </div>
          <p className="services-modal-subtitle">
            {viewMode === "table"
              ? "Usa los filtros para encontrar el servicio. Haz clic en la fila para seleccionar."
              : `Busca y asigna uno o más servicios. La suma de pasajeros debe completar los pendientes.${allowIdSearch ? " En ENAEX puedes buscar por ID o código." : ""}`}
          </p>
        </header>

        <div className="service-email-pax-summary">
          <div className="service-email-pax-chip">
            <span>Total</span>
            <strong>{totalPax}</strong>
          </div>
          <div className="service-email-pax-chip">
            <span>Asignados</span>
            <strong>{assignedAfter}</strong>
          </div>
          <div className={`service-email-pax-chip${remainingAfter === 0 ? " is-complete" : ""}`}>
            <span>Restantes</span>
            <strong>{remainingAfter}</strong>
          </div>
          <div className="service-email-pax-chip service-email-pax-chip--pending">
            <span>Pendientes ahora</span>
            <strong>{remainingBase}</strong>
          </div>
        </div>

        <div className="services-body services-body--wide">
          {existingLinks.length > 0 && (
            <div className="service-email-existing-links">
              <h3 className="service-email-existing-links-title">Ya vinculados</h3>
              <div className="service-assign-existing">
                {existingLinks.map((link) => (
                  <div key={link.id} className="service-assign-existing__item">
                    <strong>#{link.service_id ?? link.service}</strong>
                    <span>{link.passenger_count} pax</span>
                    <span>{link.service_status_label || link.service_status || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {viewMode === "table" ? (
            <ServicePickerTable
              companyId={companyId}
              serviceDate={solicitud.request_date}
              excludeIds={alreadyLinkedIds}
              selectedIds={selectedServiceIds}
              multiSelect={false}
              onSelect={(serviceId, service) => {
                const firstEmptyRow = rows.find((r) => !r.service);
                if (firstEmptyRow) {
                  updateRow(firstEmptyRow.key, { service: String(serviceId) });
                } else {
                  const newRow = createRow(String(Math.max(1, remainingBase - newAssigned)));
                  setRows((prev) => [...prev, { ...newRow, service: String(serviceId) }]);
                }
              }}
              onDeselect={(serviceId) => {
                const rowToEmpty = rows.find((r) => r.service === String(serviceId));
                if (rowToEmpty) {
                  updateRow(rowToEmpty.key, { service: "" });
                }
              }}
            />
          ) : null}

          <div className="service-email-solicitudes-header">
            <label>Servicios seleccionados ({rows.filter((r) => r.service).length})</label>
            {viewMode === "select" && (
              <button
                type="button"
                className="btn-secondary btn-secondary--compact"
                onClick={addRow}
              >
                + Agregar servicio
              </button>
            )}
          </div>

          <div className="service-assign-cards">
            {rows.map((row, index) => (
              <section key={row.key} className="service-assign-card">
                <header className="service-assign-card__top">
                  <span className="service-assign-card__index">Servicio {index + 1}</span>
                  <button
                    type="button"
                    className="service-assign-card__remove"
                    onClick={() => removeRow(row.key)}
                    disabled={rows.length === 1}
                    title="Quitar"
                    aria-label={`Quitar servicio ${index + 1}`}
                  >
                    <Trash2 size={14} strokeWidth={2} aria-hidden />
                    Quitar
                  </button>
                </header>

                {viewMode === "select" ? (
                  <>
                    <ServicePickerSelect
                      value={row.service}
                      companyId={companyId}
                      serviceDate={solicitud.request_date}
                      allowIdSearch={allowIdSearch}
                      excludeIds={[
                        ...alreadyLinkedIds,
                        ...selectedServiceIds.filter((id) => String(id) !== row.service),
                      ]}
                      onChange={(serviceId) => updateRow(row.key, { service: serviceId })}
                    />
                    {submitted && !row.service && (
                      <span className="service-email-field-error">Selecciona un servicio.</span>
                    )}
                  </>
                ) : (
                  <div className="service-assign-card__selected">
                    {row.service ? (
                      <span className="service-assign-selected-badge">
                        Servicio #{row.service} seleccionado
                      </span>
                    ) : (
                      <span className="service-assign-selected-empty">
                        Selecciona desde la tabla arriba
                      </span>
                    )}
                    {submitted && !row.service && (
                      <span className="service-email-field-error">Selecciona un servicio desde la tabla.</span>
                    )}
                  </div>
                )}

                <div className="service-assign-card__pax">
                  <label htmlFor={`pax-${row.key}`}>Cant. pasajeros a vincular</label>
                  <input
                    id={`pax-${row.key}`}
                    className="service-email-table-input service-assign-card__pax-input"
                    type="number"
                    min="1"
                    value={row.passenger_count}
                    onChange={(event) =>
                      updateRow(row.key, { passenger_count: event.target.value })
                    }
                  />
                </div>
              </section>
            ))}
          </div>

          {submitted && !countMatches && (
            <span className="service-email-field-error">
              La suma nueva ({newAssigned}) debe coincidir con los pendientes ({remainingBase}).
            </span>
          )}
          {submitted && selectedServiceIds.length !== new Set(selectedServiceIds).size && (
            <span className="service-email-field-error">No repitas el mismo servicio.</span>
          )}
        </div>

        <footer className="services-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !countMatches || remainingBase <= 0}
          >
            {loading ? "Guardando..." : "Vincular servicios"}
          </button>
        </footer>
      </form>
    </div>
  );
};

export default ServiceEmailSolicitudServicesModal;
